import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authApi } from '../api/authApi';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const loadUser = () => {
  try {
    const saved = localStorage.getItem('giggs_user');
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(loadUser);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  const persistSession = (userData, tokens) => {
    setUser(userData);
    localStorage.setItem('giggs_user', JSON.stringify(userData));
    if (tokens) localStorage.setItem('giggs_tokens', JSON.stringify(tokens));
  };

  const register = useCallback(async (data) => {
    setAuthError('');
    setLoading(true);

    try {
      const res = await authApi.register(data);
      persistSession(res.data, res.tokens);
      return res.data;
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Registration failed';
      setAuthError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password, role) => {
    setAuthError('');
    setLoading(true);
    try {
      const payload = role ? { email, password, role } : { email, password };
      const res = await authApi.login(payload);
      persistSession(res.data, res.tokens);
      return res.data;
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Login failed';
      setAuthError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setAuthError('');
    localStorage.removeItem('giggs_user');
    localStorage.removeItem('giggs_tokens');
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('giggs_user', JSON.stringify(updatedUser));
  }, []);

  // Refresh user from backend on mount. Silent on transient failure so an
  // interrupted request does not unexpectedly sign the user out.
  useEffect(() => {
    const tokens = localStorage.getItem('giggs_tokens');
    if (!tokens) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.checkAuth();
        if (cancelled) return;
        if (res?.success && res?.data) {
          setUser(res.data);
          localStorage.setItem('giggs_user', JSON.stringify(res.data));
        }
      } catch (err) {
        console.warn('check-auth refresh failed:', err.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    user,
    authError,
    loading,
    login,
    logout,
    register,
    updateUser,
    isAuthenticated: !!user,
    isClient: user?.role === 'client',
    isProvider: user?.role === 'provider',
    isAdmin: user?.role === 'admin',
    isVerified: user?.verificationStatus === 'approved',
    isPendingVerification:
      user?.role === 'provider' && user?.verificationStatus !== 'approved',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
